import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogClose 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, Expand, X } from 'lucide-react';
import { motion } from 'framer-motion';

interface ImageGalleryProps {
  images: string[];
  prompt: string;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ images, prompt }) => {
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  
  const handleDownload = (imageUrl: string) => {
    // Create a temporary anchor element
    const a = document.createElement('a');
    a.href = imageUrl;
    a.download = `generated-image-${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  
  return (
    <div className="mt-2">
      <p className="text-sm text-muted-foreground mb-2 italic">
        {prompt}
      </p>
      
      <div className={`grid gap-2 ${images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {images.map((imageUrl, index) => (
          <div 
            key={index} 
            className="relative group rounded-md overflow-hidden"
          >
            <img 
              src={imageUrl} 
              alt={`Generated image ${index + 1}`}
              className="w-full h-auto object-cover rounded-md transition-transform hover:scale-[1.02]"
            />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <Button 
                size="sm" 
                variant="ghost" 
                className="h-8 w-8 rounded-full p-0 bg-black/40 hover:bg-black/70"
                onClick={() => setExpandedImage(imageUrl)}
              >
                <Expand size={16} />
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                className="h-8 w-8 rounded-full p-0 bg-black/40 hover:bg-black/70"
                onClick={() => handleDownload(imageUrl)}
              >
                <Download size={16} />
              </Button>
            </div>
          </div>
        ))}
      </div>
      
      {/* Image expanded view modal */}
      <Dialog open={!!expandedImage} onOpenChange={(open) => !open && setExpandedImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-black/90 border-none">
          <DialogClose className="absolute right-4 top-4 z-10">
            <Button 
              variant="ghost" 
              size="icon"
              className="h-8 w-8 rounded-full bg-black/40 hover:bg-black/70 text-white"
            >
              <X size={18} />
            </Button>
          </DialogClose>
          
          {expandedImage && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="p-4 flex items-center justify-center"
            >
              <img 
                src={expandedImage} 
                alt="Expanded view"
                className="max-h-[80vh] max-w-full object-contain"
              />
            </motion.div>
          )}
          
          <div className="p-4 flex justify-end">
            <Button 
              variant="secondary" 
              size="sm"
              onClick={() => expandedImage && handleDownload(expandedImage)}
              className="gap-2"
            >
              <Download size={16} />
              Download
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageGallery; 